import React, { useEffect, useMemo, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { IMatterRequest } from '../../interface/matterRequest';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useHomeCountrySelection } from '../../hooks/useHomeCountrySelection';
import { ICountry } from '../../interface/countries';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import NoData from '../dashboard/noData';
import { CountryEmpty } from '../icons';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption, SortDirection } from '../../interface/common';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

interface CountryRadioSelectionProps {
  countryList: ICountry[];
  currentValue: string | undefined;
  handleSelectCountry: (country: ICountry) => void;
  appLanguage: string;
  sortDirection: SortDirection;
}

const CountryRadioSelection: React.FC<CountryRadioSelectionProps> = ({
  countryList,
  currentValue,
  handleSelectCountry,
  appLanguage,
  sortDirection,
}) => {
  const sortedCountryList = useMemo(
    () =>
      countryList
        ? [...countryList].sort((a, b) => {
            const comparison = getTranslationFromTranslatableEntity(
              a.translations,
              appLanguage,
            ).localeCompare(
              getTranslationFromTranslatableEntity(b.translations, appLanguage),
              appLanguage,
            );
            return sortDirection === 'DESC' ? -comparison : comparison;
          })
        : countryList,
    [countryList, appLanguage, sortDirection],
  );

  if (!countryList || countryList.length === 0) {
    return null;
  }

  return (
    <RadioGroup data-testid="radio-group" className="d-flex fd-column gap-10" value={currentValue}>
      {sortedCountryList.map((country: ICountry) => (
        <Radio
          key={country.countryCode}
          data-testid={`matter-request-country-radio-${country?.countryCode}`}
          value={country.countryCode}
          onClick={() => handleSelectCountry(country)}
        >
          <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
            {getTranslationFromTranslatableEntity(country.translations, appLanguage)}
          </Text>
        </Radio>
      ))}
    </RadioGroup>
  );
};

interface NoDataOrLoadingProps {
  countryList: ICountry[];
  isCountriesLoading: boolean;
  isFetching: boolean;
}

const NoDataOrLoading: React.FC<NoDataOrLoadingProps> = ({
  countryList,
  isCountriesLoading,
  isFetching,
}) => {
  const { t } = useTranslation();

  if (isCountriesLoading) {
    return <SkeletonOptionList />;
  }

  if (!countryList || (countryList.length === 0 && !isCountriesLoading)) {
    return (
      <div className="flex-grow d-flex justify-center items-center">
        <NoData header={t('customer:noHomeCountries')} icon={<CountryEmpty />} />
      </div>
    );
  }

  if (isFetching) {
    return <Spin data-testid="small-indicator" size="small" />;
  }

  return null;
};

const SelectHomeCountry = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { language: appLanguage } = useLanguage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const {
    countryList,
    isCountriesLoading,
    isFetching,
    setSearchValue,
    paginationRef,
    hasNextPage,
    refreshCountryList,
  } = useHomeCountrySelection();

  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    // manageMatterRequest.tsx has no discard model — this mirrors its own handleBack
    // (reset + leave). When embedded in the mail flow (transactionMail.tsx) it's not a
    // separate screen on the stack, so only one level needs to be skipped there vs two
    // for the standalone create/edit matter request route.
    updateManageMatterRequest(null, true);
    goBack(undefined, matterRequest?.isFromMailOrAppointment ? 1 : 2);
  };

  const countrySortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'name', label: t('customer:homeCountry'), type: 'text', defaultDirection: 'ASC' },
    ],
    [t],
  );
  const [countryActiveSort, setCountryActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });

  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('customer:selectHomeCountry')}
        showCross
      />,
    );
  }, []);

  const handleSelectCountry = (country: ICountry) => {
    updateManageMatterRequest({
      ...matterRequest,
      newCustomer: {
        ...matterRequest?.newCustomer,
        homeCountry: country.countryCode,
        homeCountryTranslatedName: getTranslationFromTranslatableEntity(
          country.translations,
          appLanguage,
        ),
      },
    } as unknown as IMatterRequest);
    goBackWithSelection();
  };

  return (
    <CollapsibleListLayout
      testId="home-country-selection"
      header={
        <SearchInputWithFilters
          searchTitle={t('customer:searchHomeCountries')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={refreshCountryList}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={countrySortOptions}
            activeSort={countryActiveSort}
            onApply={setCountryActiveSort}
          />
        </SearchInputWithFilters>
      }
    >
      <CountryRadioSelection
        countryList={countryList}
        currentValue={matterRequest?.newCustomer?.homeCountry}
        handleSelectCountry={handleSelectCountry}
        appLanguage={appLanguage}
        sortDirection={countryActiveSort.direction}
      />
      {hasNextPage && <div ref={paginationRef} className="loading-indicator" />}
      <NoDataOrLoading
        countryList={countryList}
        isCountriesLoading={isCountriesLoading}
        isFetching={isFetching}
      />
    </CollapsibleListLayout>
  );
};

export default SelectHomeCountry;
